/*
  7Raw product card runtime (snippet 7r-product-card, 08 A08-5, 00 §4.5 / §5.2).

  <r7-add-to-bag> wraps the card's "Add to bag +" product form:
  - with Dawn's cart drawer on the page it posts the form to the cart API together with the
    drawer's sections, publishes `cartUpdate` with source 'r7' (the header bag bump listens for it,
    00 A00-2) and lets the drawer render the new cart and open, returning focus to the bar on close;
  - errors (for example 422 over inventory) publish `cartError` and show the message above the bar;
  - without a drawer (cart type "page") or before this script runs, the form submits natively to /cart/add.
  The hover (second) image is rendered with data-src/data-srcset only; on a hover-capable device the
  first pointer or focus entry into a card's media box copies them over, and .is-loaded (set once the
  image has decoded) starts the CSS cross-fade. Touch-only devices never download it.
  Loaded with `defer` by every card; the definition and the listeners are guarded, so duplicate tags are harmless.
*/
if (!window.r7CardHoverImages) {
  window.r7CardHoverImages = true;
  const canHover = window.matchMedia('(hover: hover)');

  const loadHoverImage = (event) => {
    if (!canHover.matches || !(event.target instanceof Element)) return;
    const img = event.target.closest('.r7-card__media')?.querySelector('img.r7-card__img--hover[data-src]');
    if (!img) return;

    const markLoaded = () => img.classList.add('is-loaded');
    img.addEventListener('load', markLoaded, { once: true });
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
    if (img.complete && img.naturalWidth) markLoaded();
  };

  document.addEventListener('pointerover', loadHoverImage, { passive: true });
  document.addEventListener('focusin', loadHoverImage);
}

if (!customElements.get('r7-add-to-bag')) {
  customElements.define(
    'r7-add-to-bag',
    class R7AddToBag extends HTMLElement {
      static ERROR_MS = 6000;

      connectedCallback() {
        this.form = this.querySelector('form');
        this.button = this.form.querySelector('[type="submit"]');
        this.spinner = this.querySelector('.loading__spinner');
        this.errorEl = this.querySelector('.r7-card__error');
        this.onSubmit = this.onSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmit);
        if (document.querySelector('cart-drawer')) this.button.setAttribute('aria-haspopup', 'dialog');
      }

      disconnectedCallback() {
        this.form.removeEventListener('submit', this.onSubmit);
        clearTimeout(this.errorTimer);
      }

      onSubmit(event) {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        event.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;
        this.add(drawer);
      }

      async add(drawer) {
        this.showError('');
        this.setBusy(true);

        const formData = new FormData(this.form);
        formData.append(
          'sections',
          drawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        drawer.setActiveElement(this.button);

        const variantId = formData.get('id');
        const linesUpdate = this.createLinesUpdate(variantId, Number(formData.get('quantity')) || 1);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        try {
          const response = await fetch(routes.cart_add_url, config).then((res) => res.json());

          if (response.status) {
            const message = response.description || response.message;
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'r7',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.showError(message);
            this.dispatchCartError(message, 'INVALID');
            linesUpdate?.reject(new Error(message));
            return;
          }

          this.resolveLinesUpdate(linesUpdate);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'r7', productVariantId: variantId, cartData: response });
          drawer.classList.remove('is-empty');
          drawer.renderContents(response);
        } catch (error) {
          this.showError(window.cartStrings.error);
          this.dispatchCartError(error.message || 'Network error', 'SERVICE_UNAVAILABLE');
          linesUpdate?.reject(error);
        } finally {
          this.setBusy(false);
        }
      }

      setBusy(busy) {
        this.button.classList.toggle('loading', busy);
        this.button.toggleAttribute('aria-busy', busy);
        this.spinner.classList.toggle('hidden', !busy);
        if (busy) this.button.setAttribute('aria-disabled', 'true');
        else this.button.removeAttribute('aria-disabled');
      }

      showError(message) {
        clearTimeout(this.errorTimer);
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
        if (message) this.errorTimer = setTimeout(() => this.showError(''), R7AddToBag.ERROR_MS);
      }

      /* Shopify standard events, the same contract as Dawn's product-form. */
      createLinesUpdate(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents;
        const cartData =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((res) => res.json());

        cartData
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartError(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }
    }
  );
}
